import { Album } from 'models/album';
import { SharedAlbum } from 'networking/types/shared-album';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from 'context/app-context';
import { VaultDetailContext } from 'context/vault-detail-context';
import { addsToVault, vaultAccess } from 'helpers/sharing';
import { VaultsController } from 'networking/controllers/vaults-controller';

// The albums the vault's "My Stuff" / "Shared With Me" switch selects, for the
// Albums and Memories tabs (the dashboard applies the same rules).
//
// - The person's own vault: My Stuff is the vault; Shared With Me is the
//   albums others shared with them from other vaults (`crossVault`).
// - Someone else's vault they add to (editors and contributors): My Stuff is
//   everything in it they may see; Shared With Me only what others added
//   (`othersOnly`).
// - Someone else's vault they only see: one tab, Shared With Me, with
//   everything in it they may see (see the vault tabs bar).
export const useScopedAlbums = () => {
  const {
    state: { scope, answeredAlbums, unansweredAlbums },
  } = useContext(VaultDetailContext);
  const { state: appState } = useContext(AppContext);
  const access = vaultAccess(appState.data.user, appState.currentVault);
  const inOthersVault = !!appState.currentVault && access.role !== 'owner';
  const crossVault = scope === 'shared' && !inOthersVault;
  const othersOnly = inOthersVault && scope === 'shared' && addsToVault(access);

  const [sharedAlbums, setSharedAlbums] = useState<SharedAlbum[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!crossVault) return undefined;
    let cancelled = false;
    setLoading(true);
    VaultsController.getSharedWithMe()
      .then((albums) => !cancelled && setSharedAlbums(albums))
      .catch(() => !cancelled && setSharedAlbums([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [crossVault]);

  // Albums for the album grid: the shared ones from other vaults, or this
  // vault's (with `othersOnly`, the ones others created).
  const albums: Album[] = useMemo(() => {
    if (crossVault) return sharedAlbums as unknown as Album[];
    const all = [...answeredAlbums, ...unansweredAlbums];
    return othersOnly ? all.filter((album) => !album.myAccess?.mine) : all;
  }, [crossVault, sharedAlbums, answeredAlbums, unansweredAlbums, othersOnly]);

  // Albums for the memory list, each with its memories (with `othersOnly`,
  // the ones others added, whoever created the album).
  const memoryAlbums: Album[] = useMemo(() => {
    if (crossVault) return sharedAlbums as unknown as Album[];
    const all = [...answeredAlbums, ...unansweredAlbums];
    if (!othersOnly) return all;
    return all.map((album) => ({
      ...album,
      contents: (album.contents ?? []).filter(
        (content) => !content.myAccess?.mine,
      ),
    })) as Album[];
  }, [crossVault, sharedAlbums, answeredAlbums, unansweredAlbums, othersOnly]);

  return {
    albums,
    memoryAlbums,
    // Shared With Me in their own vault: albums from other vaults.
    crossVault,
    inOthersVault,
    // Shared With Me in someone else's vault they add to.
    othersOnly,
    loading,
    // New albums belong in My Stuff, for those who may create them.
    canCreateAlbumHere: !crossVault && !othersOnly && access.canCreateAlbum,
  };
};
